/**
 * Normalizes API/network responses into SellerSubmissionResult (Sell Your Car M2).
 */
import { SELLER_SUBMISSION_STATUSES } from "./sellerIntakeConstants";
import { createDefaultSellerSubmissionResult } from "./sellerIntakeDefaults";
import { SELLER_RESULT_CODES } from "../../intl/validationMessages.js";

function sanitizeErrorMessage(message) {
  if (!message) return "Error desconocido.";
  const text = String(message).replace(/[\r\n]+/g, " ").trim();
  if (/sql|postgres|stack|internal|exception|ECONNREFUSED/i.test(text)) {
    return "Error del servicio.";
  }
  return text.slice(0, 240);
}

function mapHttpStatus(status) {
  if (status === 201 || status === 200) return SELLER_SUBMISSION_STATUSES.RECEIVED;
  if (status === 409) return SELLER_SUBMISSION_STATUSES.DUPLICATE;
  if (status === 429) return SELLER_SUBMISSION_STATUSES.RATE_LIMITED;
  if (status === 400 || status === 422) return SELLER_SUBMISSION_STATUSES.VALIDATION_ERROR;
  if (status === 403) return SELLER_SUBMISSION_STATUSES.SPAM_REJECTED;
  if (status >= 500) return SELLER_SUBMISSION_STATUSES.SERVICE_ERROR;
  return SELLER_SUBMISSION_STATUSES.UNKNOWN_ERROR;
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionStatus} status
 * @returns {string}
 */
export function getSellerResultMessage(status) {
  return (
    SELLER_RESULT_CODES[status] ||
    SELLER_RESULT_CODES[SELLER_SUBMISSION_STATUSES.UNKNOWN_ERROR]
  );
}

/**
 * @param {object|null|undefined} responseOrError
 * @returns {import('./sellerIntakeTypes').SellerSubmissionResult}
 */
export function normalizeSellerSubmissionResult(responseOrError) {
  const base = createDefaultSellerSubmissionResult();
  const submittedAt = new Date().toISOString();

  if (!responseOrError) {
    return {
      ...base,
      status: SELLER_SUBMISSION_STATUSES.UNKNOWN_ERROR,
      sellerMessage: getSellerResultMessage(SELLER_SUBMISSION_STATUSES.UNKNOWN_ERROR),
      errorCategory: SELLER_SUBMISSION_STATUSES.UNKNOWN_ERROR,
      submittedAt,
    };
  }

  if (
    responseOrError.ok === true ||
    responseOrError.success === true ||
    responseOrError.status === SELLER_SUBMISSION_STATUSES.RECEIVED
  ) {
    const submissionId =
      responseOrError.submissionId ||
      responseOrError.referenceId ||
      responseOrError.id ||
      responseOrError.body?.submissionId ||
      responseOrError.body?.referenceId ||
      null;

    return {
      ok: true,
      status: SELLER_SUBMISSION_STATUSES.RECEIVED,
      submissionId: submissionId ? String(submissionId) : null,
      uploadToken:
        responseOrError.uploadToken ||
        responseOrError.body?.uploadToken ||
        null,
      photoUploadStatus: responseOrError.photoUploadStatus || null,
      sellerMessage: getSellerResultMessage(SELLER_SUBMISSION_STATUSES.RECEIVED),
      retryable: false,
      fieldErrors: [],
      errorCategory: null,
      submittedAt: responseOrError.submittedAt || submittedAt,
    };
  }

  if (responseOrError.name === "TypeError" && /fetch|network/i.test(String(responseOrError.message))) {
    return {
      ...base,
      status: SELLER_SUBMISSION_STATUSES.NETWORK_ERROR,
      retryable: true,
      sellerMessage: getSellerResultMessage(SELLER_SUBMISSION_STATUSES.NETWORK_ERROR),
      errorCategory: SELLER_SUBMISSION_STATUSES.NETWORK_ERROR,
      submittedAt,
    };
  }

  if (responseOrError.status === "offline" || responseOrError.offline === true) {
    return {
      ...base,
      status: SELLER_SUBMISSION_STATUSES.NETWORK_ERROR,
      retryable: true,
      sellerMessage: getSellerResultMessage(SELLER_SUBMISSION_STATUSES.NETWORK_ERROR),
      errorCategory: SELLER_SUBMISSION_STATUSES.NETWORK_ERROR,
      submittedAt,
    };
  }

  const httpStatus = Number(responseOrError.status || responseOrError.statusCode || 0);
  const statusFromBody = responseOrError.body?.status || responseOrError.statusKey;
  let status = SELLER_SUBMISSION_STATUSES.UNKNOWN_ERROR;

  if (statusFromBody && SELLER_RESULT_CODES[statusFromBody]) {
    status = statusFromBody;
  } else if (responseOrError.spam === true) {
    status = SELLER_SUBMISSION_STATUSES.SPAM_REJECTED;
  } else if (httpStatus) {
    status = mapHttpStatus(httpStatus);
  } else if (responseOrError.fieldErrors?.length || responseOrError.errors?.length) {
    status = SELLER_SUBMISSION_STATUSES.VALIDATION_ERROR;
  }

  const rawErrors = responseOrError.fieldErrors || responseOrError.errors || responseOrError.body?.errors;
  const fieldErrors = Array.isArray(rawErrors)
    ? rawErrors.map((entry) => ({
        path: entry.path ? String(entry.path) : undefined,
        message: sanitizeErrorMessage(entry.message),
      }))
    : [];

  const retryable =
    status === SELLER_SUBMISSION_STATUSES.NETWORK_ERROR ||
    status === SELLER_SUBMISSION_STATUSES.RATE_LIMITED ||
    status === SELLER_SUBMISSION_STATUSES.SERVICE_ERROR;

  const apiMessage = responseOrError.sellerMessage || responseOrError.body?.sellerMessage;
  const sellerMessage =
    typeof apiMessage === "string" && apiMessage.startsWith("validation.")
      ? apiMessage
      : getSellerResultMessage(status);

  return {
    ok: false,
    status,
    submissionId: null,
    sellerMessage,
    retryable,
    fieldErrors,
    errorCategory: status,
    submittedAt,
  };
}
